/**
 * advisor-ui.js — the plan advisor's DOM: fact-find panel, suggestion card, plans-not-shown list,
 * and the two network calls the page makes (load rules, save the session)
 * (spec docs/superpowers/specs/2026-10-12-plan-advisor-design.md, "Fact-find", "Results page").
 *
 * The logic lives in advisor-facts / advisor-rules / advisor-fit / advisor-engine; this module only
 * renders their output and reports what the agent does through the callbacks script.js passes in.
 * Every string that came from a carrier or the agent goes through escapeHtml.
 */

import { escapeHtml } from './format.js';
import { sanitizeFacts, missingFacts, CONDITIONS } from './advisor-facts.js';
import { authHeaders } from './agent-identity.js';
import { getStoredIdToken } from '../auth-config.js';

const API_BASE = '';
const MODAL_ID = 'wq-advisor-modal';

export const WARNING_LABELS = {
  provisional: 'Provisional: some fact-find answers are missing',
  check_with_carrier: 'Check eligibility with the carrier before applying',
  network_unverified: 'Doctors and drugs not verified for this plan',
  limited_detail: 'The carrier gives limited detail on this plan',
  rules_unavailable: 'Company preferences could not load, so every plan is shown',
  carrier_unavailable: 'Some carriers did not return quotes',
  session_unsaved: 'This suggestion could not be saved; it will retry on the next change',
};

const CONDITION_LABELS = {
  diabetes: 'Diabetes', heart_disease: 'Heart disease', cancer_history: 'Cancer history',
  asthma_copd: 'Asthma or COPD', mental_health: 'Mental health', joint_back: 'Joint or back', other: 'Other',
};
const ADD_ON_LABELS = { dental: 'Dental', vision: 'Vision', accident: 'Accident', criticalIllness: 'Critical illness' };
const ROLE_LABELS = { primary: 'Primary applicant', spouse: 'Spouse', child: 'Child' };

function money(n) {
  return n == null ? 'Price n/a' : '$' + Number(n).toFixed(2) + '/mo';
}

// ---------- network ----------

/**
 * GET /agents/advisor-rules. Resolves to { ruleSet, isAdmin } or null when the rules cannot load
 * (signed out, offline, server error): the engine then shows every plan with a notice.
 */
export async function loadRules(fetchImpl = fetch) {
  try {
    const res = await fetchImpl(`${API_BASE}/agents/advisor-rules`, { headers: authHeaders() });
    if (!res.ok) return null;
    const body = await res.json();
    return body && body.data ? body.data : null;
  } catch (e) {
    return null;
  }
}

/** True when this browser holds a verified sign-in, the only case in which sessions are saved. */
export function canSaveSessions() {
  return !!getStoredIdToken();
}

/**
 * POST /quotes/advisor-session. Resolves to the session id, or null on failure (the suggestion is
 * still shown; the caller retries with the next save).
 */
export async function saveSession(payload, fetchImpl = fetch) {
  if (!canSaveSessions()) return null;
  try {
    const res = await fetchImpl(`${API_BASE}/quotes/advisor-session`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) return null;
    const body = await res.json();
    return (body && body.data && body.data.sessionId) || null;
  } catch (e) {
    return null;
  }
}

// ---------- fact-find panel ----------

function radios(name, value, options) {
  return '<div class="advisor-ff__choices" role="radiogroup">' + options.map(([v, label]) =>
    '<label class="advisor-ff__choice"><input type="radio" name="' + name + '" value="' + v + '"' +
    (String(value) === String(v) ? ' checked' : '') + '> ' + escapeHtml(label) + '</label>').join('') + '</div>';
}

function memberLabel(m, i, members) {
  const childIndex = members.slice(0, i + 1).filter((x) => x.role === 'child').length;
  const base = m.role === 'child' ? 'Child ' + childIndex : ROLE_LABELS[m.role];
  return m.age != null ? base + ' (' + m.age + ')' : base;
}

function renderMember(m, i, members) {
  const yesNo = (v) => (v === true ? 'yes' : v === false ? 'no' : '');
  return '<fieldset class="advisor-ff__member" data-member="' + i + '">' +
    '<legend>' + escapeHtml(memberLabel(m, i, members)) + '</legend>' +
    '<div class="advisor-ff__q"><span>Health needs</span>' +
    radios('m' + i + '_health', m.health, [['none', 'None'], ['minor', 'Minor'], ['significant', 'Significant']]) + '</div>' +
    '<div class="advisor-ff__q"><span>Conditions (optional)</span><div class="advisor-ff__choices">' +
    CONDITIONS.map((c) => '<label class="advisor-ff__choice"><input type="checkbox" name="m' + i + '_cond" value="' + c + '"' +
      (m.conditions.includes(c) ? ' checked' : '') + '> ' + CONDITION_LABELS[c] + '</label>').join('') + '</div></div>' +
    '<div class="advisor-ff__q"><span>Planned procedure or surgery</span>' +
    radios('m' + i + '_proc', yesNo(m.plannedProcedure), [['yes', 'Yes'], ['no', 'No']]) + '</div>' +
    (m.role !== 'child'
      ? '<div class="advisor-ff__q"><span>Pregnant or planning a pregnancy</span>' +
        radios('m' + i + '_preg', yesNo(m.pregnant), [['yes', 'Yes'], ['no', 'No']]) + '</div>'
      : '') +
    '</fieldset>';
}

/** Build the fact-find form's HTML. Exported for tests. */
export function factFindHtml(factsIn, { doctorsEditable = false } = {}) {
  const f = sanitizeFacts(factsIn);
  const missing = missingFacts(f);
  const people = f.doctors.map((d) => escapeHtml(d.name || d.npi)).concat(f.drugs.map((d) => escapeHtml(d.name || d.rxcui)));
  return '<form class="advisor-ff" novalidate>' +
    '<section class="advisor-ff__section"><h3>Budget</h3>' +
    '<label class="advisor-ff__budget">Monthly budget $ <input type="number" name="budgetMonthly" min="1" step="1" inputmode="numeric" value="' +
    (f.budgetMonthly != null ? f.budgetMonthly : '') + '"></label></section>' +
    '<section class="advisor-ff__section"><h3>Health, per person</h3>' +
    f.members.map((m, i) => renderMember(m, i, f.members)).join('') + '</section>' +
    '<section class="advisor-ff__section"><h3>Expected use of care</h3>' +
    radios('usage', f.usage, [['low', 'Low'], ['moderate', 'Moderate'], ['high', 'High']]) + '</section>' +
    '<section class="advisor-ff__section"><h3>What matters most</h3>' +
    radios('priority', f.priority, [['lowest_price', 'Lowest price'], ['best_coverage', 'Best coverage'], ['keep_doctors', 'Keep doctors'], ['balanced', 'Balanced']]) + '</section>' +
    '<section class="advisor-ff__section"><h3>Wants (optional)</h3>' +
    '<label class="advisor-ff__choice"><input type="checkbox" name="wantHsa"' + (f.wants.hsa ? ' checked' : '') + '> HSA-eligible</label> ' +
    '<label>Network <select name="wantNetwork">' +
    [['any', 'No preference'], ['hmo', 'HMO'], ['epo', 'EPO'], ['ppo', 'PPO']].map(([v, l]) =>
      '<option value="' + v + '"' + ((f.wants.networkType || 'any') === v ? ' selected' : '') + '>' + l + '</option>').join('') +
    '</select></label></section>' +
    '<section class="advisor-ff__section"><h3>Add-ons the client is interested in</h3><div class="advisor-ff__choices">' +
    Object.keys(ADD_ON_LABELS).map((k) => '<label class="advisor-ff__choice"><input type="checkbox" name="addon_' + k + '"' +
      (f.addOns[k] ? ' checked' : '') + '> ' + ADD_ON_LABELS[k] + '</label>').join('') + '</div></section>' +
    '<section class="advisor-ff__section"><h3>Doctors and prescriptions</h3>' +
    '<p class="advisor-ff__hint">' + (people.length ? people.join(', ') : 'None added.') + '</p>' +
    (doctorsEditable
      ? '<button type="button" class="btn-text-link" data-advisor-doctors>Add or edit doctors and prescriptions</button>'
      : '<p class="advisor-ff__hint">Add them on the results page with Doctors &amp; Rx.</p>') +
    '</section>' +
    '<p class="advisor-ff__status" role="status">' + (missing.length
      ? missing.length + ' required answer' + (missing.length === 1 ? '' : 's') + ' left. The suggestion stays provisional until then.'
      : 'All required answers are in.') + '</p>' +
    '<div class="advisor-ff__actions"><button type="button" class="btn-text" data-advisor-cancel>Cancel</button>' +
    '<button type="submit" class="btn-primary">Save fact-find</button></div>' +
    '</form>';
}

/** Read the form back into facts, starting from the facts it was opened with. */
export function readFactFindForm(form, baseFacts) {
  const base = sanitizeFacts(baseFacts);
  const val = (name) => {
    const el = form.querySelector('[name="' + name + '"]:checked') || form.querySelector('select[name="' + name + '"]');
    return el ? el.value : null;
  };
  const yesNo = (v) => (v === 'yes' ? true : v === 'no' ? false : null);
  const budgetEl = form.querySelector('[name="budgetMonthly"]');
  const members = base.members.map((m, i) => ({
    ...m,
    health: val('m' + i + '_health'),
    conditions: [...form.querySelectorAll('[name="m' + i + '_cond"]:checked')].map((el) => el.value),
    plannedProcedure: yesNo(val('m' + i + '_proc')),
    pregnant: m.role === 'child' ? false : yesNo(val('m' + i + '_preg')),
  }));
  const addOns = {};
  Object.keys(ADD_ON_LABELS).forEach((k) => {
    const el = form.querySelector('[name="addon_' + k + '"]');
    addOns[k] = !!(el && el.checked);
  });
  const hsaEl = form.querySelector('[name="wantHsa"]');
  return sanitizeFacts({
    ...base,
    budgetMonthly: budgetEl ? budgetEl.value : null,
    members,
    usage: val('usage'),
    priority: val('priority'),
    wants: { hsa: hsaEl && hsaEl.checked ? true : null, networkType: val('wantNetwork') },
    addOns,
  });
}

function ensureModal() {
  let modal = document.getElementById(MODAL_ID);
  if (modal) return modal;
  modal = document.createElement('div');
  modal.id = MODAL_ID;
  modal.className = 'wq-modal';
  modal.innerHTML = '<div class="wq-modal__backdrop" data-advisor-cancel></div>' +
    '<div class="wq-modal__content wq-advisor-modal-content" role="dialog" aria-modal="true" aria-labelledby="wq-advisor-title">' +
    '<button type="button" class="wq-modal__close" aria-label="Close fact-find" data-advisor-cancel>&times;</button>' +
    '<h2 id="wq-advisor-title">Fact-find</h2>' +
    '<p class="wq-modal__subtitle">Answers drive the plan suggestion. Household, ZIP and income come from the quote.</p>' +
    '<div class="wq-advisor-body"></div></div>';
  document.body.appendChild(modal);
  return modal;
}

let escHandler = null;

export function closeFactFind() {
  const modal = document.getElementById(MODAL_ID);
  if (modal) modal.classList.remove('wq-modal--open');
  if (escHandler) document.removeEventListener('keydown', escHandler);
  escHandler = null;
}

/**
 * Open the fact-find.
 * @param {{ facts: Object, onSave: Function, onEditDoctors?: Function }} opts
 */
export function openFactFind({ facts, onSave, onEditDoctors }) {
  const modal = ensureModal();
  const body = modal.querySelector('.wq-advisor-body');
  body.innerHTML = factFindHtml(facts, { doctorsEditable: typeof onEditDoctors === 'function' });
  const form = body.querySelector('form');
  modal.querySelectorAll('[data-advisor-cancel]').forEach((el) => { el.onclick = closeFactFind; });
  const doctorsBtn = body.querySelector('[data-advisor-doctors]');
  if (doctorsBtn) {
    doctorsBtn.addEventListener('click', () => {
      const draft = readFactFindForm(form, facts);
      closeFactFind();
      onEditDoctors(draft);
    });
  }
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const next = readFactFindForm(form, facts);
    closeFactFind();
    if (typeof onSave === 'function') onSave(next);
  });
  modal.classList.add('wq-modal--open');
  escHandler = (e) => { if (e.key === 'Escape') closeFactFind(); };
  document.addEventListener('keydown', escHandler);
  const first = form.querySelector('input');
  if (first) first.focus();
}

// ---------- suggestion card ----------

function tierBadge(entry) {
  if (!entry || !entry.tierLabel || entry.tier === 2) return '';
  return '<span class="advisor-tier advisor-tier--' + entry.tier + '">' + escapeHtml(entry.tierLabel) + '</span>';
}

function entryRow(entry, kind, inCart) {
  return '<li class="advisor-alt" data-plan-id="' + escapeHtml(entry.planId) + '" data-kind="' + kind + '">' +
    '<span class="advisor-alt__name"><span class="featured-pkg__carrier">' + escapeHtml(entry.carrier) + '</span> ' +
    escapeHtml(entry.name) + '</span>' + tierBadge(entry) +
    '<span class="advisor-alt__fit">' + Math.round(entry.fit) + ' fit</span>' +
    '<span class="featured-pkg__price">' + money(entry.premium) + '</span>' +
    '<button type="button" class="btn-text-link" data-advisor-add="' + escapeHtml(entry.planId) + '">' +
    (inCart ? 'In cart' : 'Add') + '</button></li>';
}

function warningsHtml(warnings, missingCount) {
  if (!warnings || !warnings.length) return '';
  return '<ul class="advisor-warnings">' + warnings.map((w) => {
    let label = WARNING_LABELS[w] || w;
    if (w === 'provisional' && missingCount) label = 'Provisional: ' + missingCount + ' required fact-find answer' + (missingCount === 1 ? '' : 's') + ' missing';
    return '<li data-warning="' + escapeHtml(w) + '">' + escapeHtml(label) + '</li>';
  }).join('') + '</ul>';
}

function hiddenListHtml(hidden) {
  return '<ul class="advisor-hidden__list">' + hidden.map((h) =>
    '<li data-group="' + escapeHtml(h.groupKey) + '">' +
    '<span class="advisor-alt__name"><span class="featured-pkg__carrier">' + escapeHtml(h.carrier) + '</span> ' + escapeHtml(h.name) + '</span>' +
    '<span class="advisor-hidden__reason">' + escapeHtml(h.reason || '') + '</span>' +
    '<span class="featured-pkg__price">from ' + money(h.premiumFrom) + '</span>' +
    (h.revealed
      ? '<span class="advisor-hidden__shown">Shown</span>'
      : '<button type="button" class="btn-text-link" data-advisor-reveal="' + escapeHtml(h.groupKey) + '">Add to quote</button>') +
    '</li>').join('') + '</ul>';
}

/** Build the suggestion card's HTML. Exported for tests. */
export function suggestionCardHtml(result, { isInCart = () => false, hiddenOpen = false, editRulesUrl = null } = {}) {
  const s = result.suggestion;
  const missingCount = (result.missingFacts || []).length;
  let main = '';
  if (s) {
    main = '<div class="featured-pkg__head">' +
      '<span class="plan-badge plan-badge--recommended">Advisor suggestion</span>' + tierBadge(s) +
      '<span class="featured-pkg__match">' + Math.round(s.fit) + ' fit</span>' +
      '</div>' +
      '<div class="featured-pkg__plan"><span class="featured-pkg__carrier">' + escapeHtml(s.carrier) +
      '</span><span class="featured-pkg__name">' + escapeHtml(s.name) + '</span>' +
      '<span class="featured-pkg__price">' + money(s.premium) + '</span></div>' +
      (s.reasons.length ? '<ul class="featured-pkg__reasons">' + s.reasons.slice(0, 3).map((r) => '<li>' + escapeHtml(r) + '</li>').join('') + '</ul>' : '') +
      warningsHtml(result.warnings, missingCount) +
      '<div class="featured-pkg__foot"><button type="button" class="btn-primary featured-pkg__add' + (isInCart(s) ? ' is-in' : '') +
      '" data-advisor-add="' + escapeHtml(s.planId) + '">' + (isInCart(s) ? 'Remove from cart' : 'Add to cart') + '</button></div>';
    if (result.alternatives.length) {
      main += '<h4 class="advisor-sub">Alternatives</h4><ul class="advisor-alts">' +
        result.alternatives.map((a) => entryRow(a, 'alternative', isInCart(a))).join('') + '</ul>';
    }
  } else if (result.outcome === 'no_strong_fit') {
    main = '<div class="featured-pkg__head"><span class="plan-badge">No strong fit</span>' +
      '<p class="featured-pkg__blurb">No plan clears the minimum fit. These come closest.</p></div>' +
      warningsHtml(result.warnings, missingCount) +
      '<ul class="advisor-alts">' + result.closestMatches.map((a) => entryRow(a, 'closest', isInCart(a))).join('') + '</ul>';
  } else {
    main = '<div class="featured-pkg__head"><span class="plan-badge">Advisor</span>' +
      '<p class="featured-pkg__blurb">No medical plans to suggest from this quote.</p></div>' +
      warningsHtml(result.warnings, missingCount);
  }
  const addOns = Object.keys(result.addOns || {}).filter((k) => result.addOns[k]);
  if (addOns.length) {
    main += '<h4 class="advisor-sub">Add-ons</h4><ul class="advisor-alts">' +
      addOns.map((k) => entryRow(result.addOns[k], 'addon', isInCart(result.addOns[k]))).join('') + '</ul>';
  }
  const hidden = result.hidden || [];
  const notShown = hidden.filter((h) => !h.revealed).length;
  const hiddenHtml = hidden.length
    ? '<div class="advisor-hidden"><button type="button" class="btn-text-link" data-advisor-hidden-toggle aria-expanded="' + hiddenOpen + '">' +
      notShown + ' plan' + (notShown === 1 ? '' : 's') + ' not shown</button>' +
      (hiddenOpen ? hiddenListHtml(hidden) : '') + '</div>'
    : '';
  return '<div class="featured-pkg featured-pkg--rec plan-card--featured advisor-card" data-outcome="' + result.outcome + '">' +
    main +
    '<div class="advisor-card__tools"><button type="button" class="btn-text-link" data-advisor-factfind>' +
    (missingCount ? 'Complete fact-find' : 'Edit fact-find') + '</button>' + hiddenHtml +
    (editRulesUrl ? '<a class="btn-text-link advisor-card__rules" href="' + escapeHtml(editRulesUrl) + '">Edit company rules</a>' : '') +
    '</div>' +
    '</div>';
}

/**
 * Render the card into its slot and wire its buttons.
 * @param {HTMLElement} slot
 * @param {Object} result - runAdvisor output
 * @param {{ isInCart, onToggleCart(entry), onReveal(groupKey), onFactFind(), editRulesUrl? }} handlers
 */
export function renderSuggestionCard(slot, result, handlers) {
  if (!slot) return;
  const state = renderSuggestionCard._state || (renderSuggestionCard._state = { hiddenOpen: false });
  slot.hidden = false;
  slot.innerHTML = suggestionCardHtml(result, {
    isInCart: handlers.isInCart, hiddenOpen: state.hiddenOpen, editRulesUrl: handlers.editRulesUrl || null,
  });
  const byId = new Map();
  [result.suggestion, ...result.alternatives, ...result.closestMatches, ...Object.values(result.addOns || {})]
    .filter(Boolean).forEach((e) => byId.set(String(e.planId), e));
  slot.querySelectorAll('[data-advisor-add]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const entry = byId.get(btn.getAttribute('data-advisor-add'));
      if (entry) handlers.onToggleCart(entry);
    });
  });
  slot.querySelectorAll('[data-advisor-reveal]').forEach((btn) => {
    btn.addEventListener('click', () => handlers.onReveal(btn.getAttribute('data-advisor-reveal')));
  });
  const toggle = slot.querySelector('[data-advisor-hidden-toggle]');
  if (toggle) {
    toggle.addEventListener('click', () => {
      state.hiddenOpen = !state.hiddenOpen;
      renderSuggestionCard(slot, result, handlers);
    });
  }
  const ff = slot.querySelector('[data-advisor-factfind]');
  if (ff) ff.addEventListener('click', () => handlers.onFactFind());
}

export const AdvisorUI = {
  WARNING_LABELS, loadRules, canSaveSessions, saveSession,
  factFindHtml, readFactFindForm, openFactFind, closeFactFind, suggestionCardHtml, renderSuggestionCard,
};
